import { type Money, moneyTowardProgress } from '@taler/domain'
import { computed, type ComputedRef, onBeforeUnmount, ref, watch } from 'vue'

const DEFAULT_DURATION_MS = 480

/** cubic-bezier(0.16, 1, 0.3, 1), the design system's --ease-out: monotonic, no overshoot. */
function easeOut(t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t
  return 1 - (1 - clamped) ** 3
}

export interface UseCountUpOptions {
  /** `undefined` while the true value is not yet known - the figure never starts counting toward a guess. */
  readonly value: () => Money | undefined
  readonly reducedMotion: () => boolean
  readonly durationMs?: number
  readonly now?: () => number
  readonly schedule?: (callback: (time: number) => void) => number
  readonly cancel?: (handle: number) => void
}

export interface UseCountUp {
  readonly displayed: ComputedRef<Money | undefined>
  readonly animating: ComputedRef<boolean>
}

/**
 * §6 Rule 3: a hero figure counts from zero to its exact value once, on first arrival,
 * and only then. Every later value - a currency switch, a week-window change, a refetch -
 * replaces the figure directly; it is a change to an already-shown value, not an arrival.
 */
export function useCountUp(options: UseCountUpOptions): UseCountUp {
  const now = options.now ?? (() => performance.now())
  const schedule = options.schedule ?? ((callback) => requestAnimationFrame(callback))
  const cancel = options.cancel ?? ((handle) => cancelAnimationFrame(handle))
  const duration = options.durationMs ?? DEFAULT_DURATION_MS

  const displayed = ref<Money | undefined>(undefined)
  const animating = ref(false)
  let hasArrived = false
  let handle: number | undefined

  const stop = (): void => {
    if (handle !== undefined) cancel(handle)
    handle = undefined
  }

  // The final frame is the real value, assigned - never the last interpolation step.
  const settle = (target: Money): void => {
    stop()
    displayed.value = target
    animating.value = false
  }

  const animate = (target: Money): void => {
    stop()
    if (target.units === 0n) {
      settle(target)
      return
    }
    animating.value = true
    // Visible at zero from the first tick, not only once the first scheduled frame paints.
    displayed.value = moneyTowardProgress(target, 0)
    const start = now()
    const step = (time: number): void => {
      const elapsed = time - start
      if (elapsed >= duration) {
        settle(target)
        return
      }
      displayed.value = moneyTowardProgress(target, easeOut(elapsed / duration))
      handle = schedule(step)
    }
    handle = schedule(step)
  }

  watch(
    () => options.value(),
    (value) => {
      if (value === undefined) return
      if (!hasArrived) {
        hasArrived = true
        if (options.reducedMotion()) settle(value)
        else animate(value)
        return
      }
      settle(value)
    },
    // Sync, not batched: the count-up starts the instant the true value resolves.
    { immediate: true, flush: 'sync' },
  )

  onBeforeUnmount(stop)

  return { displayed: computed(() => displayed.value), animating: computed(() => animating.value) }
}
